import { useMemo } from "react"
import { useCompanyUserControllerGetAllCompanyUsersQuery } from "~/ui-rtk/api/companyUserApi"
import { useUserControllerGetSuperAdminsQuery } from "~/ui-rtk/api/userApi"
import { useAppSelector } from "~/ui-rtk/app/hooks"
import { selectUser } from "~/ui-rtk/app/selectors/user.selector"
import useAccess from "~/ui-rtk/hooks/access"

export type AssignableUser = {
  id: string
  name: string
  /** The logged-in user. Always present and first in the list. */
  isSelf: boolean
  /** Super admin who is not a member of the current company. */
  isExternalSuperAdmin: boolean
}

/**
 * Users an agenda slot can be assigned to: the logged-in user (always, and
 * first), the current company's members, plus — for super admins only — all
 * super admin users. GET /user/super-admins is also gated server-side
 * (AUTH_SUPER_ADMIN scope); the skip just avoids a guaranteed 403 for
 * regular users.
 */
export function useAssignableUsers() {
  const { isSuperAdmin } = useAccess()
  const currentUser = useAppSelector(selectUser)

  const { data: companyUsers = [], isLoading } =
    useCompanyUserControllerGetAllCompanyUsersQuery()
  const { data: superAdmins = [] } = useUserControllerGetSuperAdminsQuery(
    undefined,
    { skip: !isSuperAdmin },
  )

  const users = useMemo<AssignableUser[]>(() => {
    const companyIds = new Set(companyUsers.map(user => user.id))
    const merged = [
      ...companyUsers.map(user => ({
        id: user.id,
        name: user.name || user.email,
        isSelf: user.id === currentUser.id,
        isExternalSuperAdmin: false,
      })),
      ...superAdmins
        .filter(user => !companyIds.has(user.id))
        .map(user => ({
          id: user.id,
          name: user.name || user.email,
          isSelf: user.id === currentUser.id,
          isExternalSuperAdmin: true,
        })),
    ]

    // Always self-assignable, even when the logged-in user is in neither list
    // (e.g. a super admin viewing a company they are not a member of before
    // the super-admins query resolves).
    const self = merged.find(user => user.isSelf) ?? {
      id: currentUser.id,
      name: currentUser.name || currentUser.email,
      isSelf: true,
      isExternalSuperAdmin: !companyIds.has(currentUser.id),
    }

    return [self, ...merged.filter(user => !user.isSelf)]
  }, [companyUsers, superAdmins, currentUser])

  return { users, isLoading }
}
